import React, { useState } from 'react';
import { TaskItem } from '../../../types/tasks';
import { useTasks } from '../../../context/TasksContext';
import { shortDate } from '../../../utils/taskDates';
import { userName } from '../../../utils/taskUsers';
import { Workload, workload } from '../../../utils/projectDashboard';
import { isLate, rangeLabel } from '../../../utils/projectPlan';
import TaskIcon from '../../tasks/TaskIcon';
import { UserBubble } from '../../tasks/UserBubble';
import { DashCard, DashRow } from './DashCard';

const WEEKS = 6;

/** How busy a week is, for the cell's shade. */
const level = (n: number) => (n === 0 ? 0 : n <= 2 ? 1 : n <= 4 ? 2 : 3);

/** Which number was clicked: a column, or a week by its index. */
type Pick = { email: string; col: 'open' | 'overdue' | 'critical' | number };

const byWhen = (a: TaskItem, b: TaskItem) =>
  (a.plan_start || a.due_date || '9999').localeCompare(b.plan_start || b.due_date || '9999');

/**
 * Who has how much on: each person's open tasks (overdue and critical called
 * out) and, week by week, how many planned tasks land on them — so a pile-up
 * before a race shows before it happens. Click a number to list those tasks.
 */
const WorkloadCard: React.FC<{
  items: TaskItem[];
  onOpenItem: (item: TaskItem) => void;
  locked: (i: TaskItem) => boolean;
  /** Where each task sits ("Project › Branch"). */
  where: (i: TaskItem) => string | undefined;
}> = ({ items, onOpenItem, locked, where }) => {
  const { users, today } = useTasks();
  const { weekStarts, people } = workload(items, today, WEEKS);
  const [pick, setPick] = useState<Pick | null>(null);

  const isPicked = (email: string, col: Pick['col']) => pick?.email === email && pick.col === col;
  const toggle = (email: string, col: Pick['col']) => setPick(isPicked(email, col) ? null : { email, col });

  // A number that opens its tasks; a dash when there are none.
  const cell = (p: Workload, col: Pick['col'], n: number, className: string, title: string, blank = '—') => (
    <span className={`${className}${isPicked(p.email, col) ? ' is-picked' : ''}`} role="cell">
      {n > 0 ? (
        <button
          type="button"
          className="tp-w-open"
          onClick={() => toggle(p.email, col)}
          title={title}
          aria-expanded={isPicked(p.email, col)}
        >
          {n}
        </button>
      ) : (
        blank
      )}
    </span>
  );

  const picked = pick && people.find((p) => p.email === pick.email);
  const pickedTasks = picked
    ? [...(typeof pick!.col === 'number' ? picked.tasks.weeks[pick!.col] || [] : picked.tasks[pick!.col])].sort(byWhen)
    : [];
  const pickedName = picked ? (picked.email ? userName(users, picked.email) : 'Nobody yet') : '';
  const pickedWhat = !pick
    ? ''
    : typeof pick.col === 'number'
      ? pick.col === 0 ? 'this week' : `the week of ${shortDate(weekStarts[pick.col])}`
      : pick.col === 'open' ? 'open' : pick.col === 'overdue' ? 'overdue' : 'on the critical path';

  const flagFor = (i: TaskItem) =>
    i.due_date && i.due_date < today
      ? { label: 'Overdue', tone: 'danger' as const, date: i.due_date }
      : isLate(i)
        ? { label: `${-i.slack_days!}d late`, tone: 'danger' as const }
        : i.plan_start && i.plan_finish
          ? { label: rangeLabel(i.plan_start, i.plan_finish), tone: 'muted' as const }
          : i.due_date
            ? { label: 'Due', tone: 'info' as const, date: i.due_date }
            : undefined;

  return (
    <DashCard title="Workload" icon="assigned" wide empty="" tone="normal">
      {people.length === 0 ? (
        <p className="tl-empty">No open work yet.</p>
      ) : (
        <>
          <div className="tp-w-table" role="table" aria-label="Workload">
            <div className="tp-w-row tp-w-headrow" role="row">
              <span className="tp-w-person" role="columnheader">Person</span>
              <span className="tp-w-num" role="columnheader" title="Open tasks">Open</span>
              <span className="tp-w-num" role="columnheader" title="Past their due date">Overdue</span>
              <span className="tp-w-num" role="columnheader" title="On the critical path">Critical</span>
              {weekStarts.map((w, i) => (
                <span key={w} className="tp-w-week" role="columnheader" title={`Week of ${shortDate(w)}`}>
                  {i === 0 ? 'This wk' : shortDate(w)}
                </span>
              ))}
            </div>
            {people.map((p) => (
              <div key={p.email || 'nobody'} className="tp-w-row" role="row">
                <span className="tp-w-person" role="cell">
                  {p.email ? (
                    <UserBubble users={users} email={p.email} size="md" title="" />
                  ) : (
                    <span className="tk-bubble tk-bubble-md tk-bubble-empty"><TaskIcon name="user" size={13} /></span>
                  )}
                  <span>{p.email ? userName(users, p.email) : 'Nobody yet'}</span>
                </span>
                {cell(p, 'open', p.open, 'tp-w-num', 'Show open tasks')}
                {cell(p, 'overdue', p.overdue, `tp-w-num${p.overdue ? ' is-danger' : ''}`, 'Show overdue tasks')}
                {cell(p, 'critical', p.critical, `tp-w-num${p.critical ? ' is-critical' : ''}`, 'Show tasks on the critical path')}
                {p.weeks.map((n, i) =>
                  <React.Fragment key={weekStarts[i]}>
                    {cell(
                      p,
                      i,
                      n,
                      `tp-w-week tp-w-cell is-l${level(n)}`,
                      `${n} planned task${n === 1 ? '' : 's'} the week of ${shortDate(weekStarts[i])} — show them`,
                      ''
                    )}
                  </React.Fragment>
                )}
              </div>
            ))}
          </div>

          {picked && (
            <div className="tp-w-detail">
              <div className="tp-w-detail-head">
                <strong>{pickedName}</strong>
                <span className="tk-muted">
                  {pickedTasks.length} task{pickedTasks.length === 1 ? '' : 's'} {pickedWhat}
                </span>
                <button type="button" className="tk-btn tk-btn-ghost tk-btn-sm" onClick={() => setPick(null)} aria-label="Close">
                  <TaskIcon name="close" size={14} />
                </button>
              </div>
              <ul className="tp-d-list">
                {pickedTasks.map((i) => (
                  <DashRow key={i.id} item={i} flag={flagFor(i)} context={where(i)} readOnly={locked(i)} onOpen={onOpenItem} />
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </DashCard>
  );
};

export default WorkloadCard;
